import { useEffect, useState } from 'react';
import { useRouteError } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { ExpandableErrorAlert } from './components/ExpandableErrorAlert';
import { Logo } from './components/Logo/Logo';
import { useDatadogContext } from './context/hooks/useDatadogContext';
import { Button } from './design-system';
import { useAuth } from './hooks/useAuth/useAuth';
import { LATEST_ORGANIZATION_STORAGE_KEY } from './hooks/useStoredOrganizationId/constants';
import './index.css';
import { LATEST_TENANT_STORAGE_KEY } from './utils/constants';
import { getCurrentEnv } from './utils/get-current-env';
import { isError } from './utils/is-error';
import { useLocalStorageState } from './utils/use-local-storage-state';

export function RouteErrorElement() {
  const { datadogLogs } = useDatadogContext();
  const error = useRouteError();
  const [staleRedirect, setStaleRedirect] = useState(false);

  const [uuid, setUuid] = useState('');
  const [orgId] = useLocalStorageState(LATEST_ORGANIZATION_STORAGE_KEY, '');
  const [tenantId] = useLocalStorageState(LATEST_TENANT_STORAGE_KEY, '');
  const auth = useAuth();

  const importError =
    isError(error) &&
    (error.message.includes('Loading chunk') ||
      error.message.includes('Failed to fetch dynamically imported module'))
      ? error.message
      : null;

  useEffect(() => {
    if (isError(error)) {
      const crashId = uuidv4();
      setUuid(crashId);

      datadogLogs?.logger?.error(
        error.message || 'Page crash',
        {
          frontend_crash_id: crashId,
          org_id: orgId,
          tenant_id: tenantId,
          user_email: auth?.session?.user.email,
          env: getCurrentEnv(),
        },
        error
      );
    }
  }, [error, orgId, auth?.session?.user.email, datadogLogs, tenantId]);

  useEffect(() => {
    if (!importError) return;

    // This error would happen when a new version of the app is deployed
    // and the previous deployment's chunks are no longer fetchable.
    // Reloading the page will load the new version of the app and fix it.
    if (window.sessionStorage.getItem('lc_last_seen_error') !== importError) {
      // This avoids an infinite loop of reloading the page,
      // ie. if reloading the page still results in the same error,
      // no need to reload again.
      window.sessionStorage.setItem('lc_last_seen_error', importError);
      if (isError(error))
        datadogLogs?.logger?.error(
          `Page reloaded due to import error: ${importError}`,
          {},
          error
        );
      const timeoutId = setTimeout(() => {
        const url = new URL(window.location.href);
        url.searchParams.set('refresh', Date.now().toString());
        window.location.href = url.toString();
      }, 2000);
      return () => clearTimeout(timeoutId);
    } else {
      setStaleRedirect(true);
      if (isError(error))
        datadogLogs?.logger?.error(
          `Import error seen again after page reload: ${importError}`,
          {},
          error
        );
    }
  }, [importError, error]);

  if (importError && !staleRedirect) return null;
  return <ErrorElement error={error} errorUUID={uuid} />;
}

export function ErrorElement({
  error,
  errorUUID,
}: {
  error: unknown;
  errorUUID?: string;
}) {
  return (
    <div className="fixed z-0 flex h-screen w-screen items-center justify-center bg-secondary">
      <main className="mx-4 w-full max-w-[480px] rounded-lg border border-secondary bg-popover p-6 text-primary">
        <div className="mb-2 text-3xl">
          <Logo />
        </div>
        <h2 className="mb-4 text-lg font-semibold">
          An unexpected error has occurred.
        </h2>
        {errorUUID && <p className="mb-4">Error ID: {errorUUID}</p>}

        {isError(error) && (
          <div className="mb-4 max-h-[300px] overflow-y-auto">
            <ExpandableErrorAlert error={`${error.message}`} />
          </div>
        )}
        <p className="mb-4">
          Our team has been notified. Please reach us at{' '}
          <a
            href="mailto:support@langchain.dev"
            className="text-brand-green-400 hover:text-brand-green-400"
          >
            support@langchain.dev
          </a>{' '}
          for further assistance.
        </p>

        <Button
          onClick={() => window.location.reload()}
          variant="outlined"
          color="secondary"
          className="w-full"
          size="md"
        >
          Reload page
        </Button>
      </main>
    </div>
  );
}
